import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  InputAdornment,
  Typography,
} from '@mui/material';
import { Wallet, MessageSquare } from 'lucide-react';
import { getStripe } from '../../utils/stripeClient';
import axios from 'axios';
import {
  acme,
  acmeBillingSectionContentSx,
  acmePageSectionCardSx,
  acmePlanCardSx,
  acmePlanBadgeSx,
  acmeTextFieldSx,
  acmeIconTones,
} from '../../theme/acmeTheme';
import { Card, CardContent, Button, Chip, Input, PageBlockTitle } from '../ui';
import SmsProviderBalancePanel from '../SmsProviderBalancePanel/SmsProviderBalancePanel';

function formatMoney(value, currency = 'EUR') {
  const n = Number(value) || 0;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
}

function formatCredits(value) {
  const n = Math.max(0, Math.floor(Number(value) || 0));
  return n.toLocaleString();
}

function niceRoundCredits(credits) {
  const n = Math.max(0, Math.floor(Number(credits) || 0));
  if (n === 0) return 0;
  const increment = n < 1000 ? 10 : n < 10000 ? 100 : 500;
  return Math.ceil(n / increment) * increment;
}

export default function SmsBillingTab({ user, onError, onInfo }) {
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customAmount, setCustomAmount] = useState('');
  const [busyId, setBusyId] = useState(null);

  const currency = wallet?.currency || 'EUR';

  const creditsPerEur = (() => {
    const base = (wallet?.packages || []).find((p) => p.bonusPercent === 0);
    return base && base.amount ? base.baseCredits / base.amount : 100;
  })();

  const customBonusPercent = (amt) => {
    if (amt >= 250) return 15;
    if (amt >= 100) return 10;
    if (amt >= 50) return 5;
    return 0;
  };

  const estimatedCustomCredits = (() => {
    const amt = parseFloat(customAmount);
    if (Number.isNaN(amt) || amt <= 0) return null;
    const base = Math.floor(amt * creditsPerEur);
    const raw = base + Math.floor((base * customBonusPercent(amt)) / 100);
    return niceRoundCredits(raw);
  })();

  const loadWallet = async () => {
    try {
      const { data } = await axios.get('/api/sms-billing/wallet');
      setWallet(data);
    } catch (err) {
      onError?.(err.response?.data?.error || 'Could not load SMS balance.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWallet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCheckout = async (amount, id) => {
    const billingComplete =
      user?.billingAddress &&
      user?.billingCity &&
      user?.billingPostalCode &&
      user?.billingCountry;
    if (!billingComplete) {
      onError?.(
        'Please complete your billing information (address, city, postal code, country) first.',
      );
      return;
    }

    setBusyId(id);
    try {
      const { data } = await axios.post('/api/sms-billing/create-checkout-session', { amount });
      const stripe = await getStripe();
      if (!stripe) {
        onError?.('Stripe is not configured. Set REACT_APP_STRIPE_PUBLIC_KEY in frontend/.env');
        return;
      }
      const { error } = await stripe.redirectToCheckout({ sessionId: data.sessionId });
      if (error) {
        onError?.(error.message || 'Failed to start checkout.');
      }
    } catch (err) {
      onError?.(err.response?.data?.error || 'Failed to start checkout. Try again.');
    } finally {
      setBusyId(null);
    }
  };

  const handleCustomTopup = () => {
    const amount = parseFloat(customAmount);
    const min = wallet?.minTopup ?? 5;
    const max = wallet?.maxTopup ?? 5000;
    if (Number.isNaN(amount) || amount < min) {
      onError?.(`Minimum top-up is ${formatMoney(min, currency)}.`);
      return;
    }
    if (amount > max) {
      onError?.(`Maximum top-up is ${formatMoney(max, currency)}.`);
      return;
    }
    startCheckout(amount, 'custom');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} sx={{ color: acme.colors.iconDefault }} />
      </Box>
    );
  }

  const packages = wallet?.packages || [];

  return (
    <Box sx={acmePageSectionCardSx}>
      <SmsProviderBalancePanel user={user} variant="inline" />

      <Card
        sx={{
          mb: 3,
          boxShadow: 'none',
          background: `linear-gradient(135deg, ${acme.colors.foreground} 0%, #27272a 100%)`,
          color: acme.colors.background,
        }}
      >
        <CardContent sx={{ ...acmeBillingSectionContentSx, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Wallet size={36} strokeWidth={1.5} color={acmeIconTones.muted} />
          <Box>
            <Typography sx={{ fontSize: '0.875rem', opacity: 0.85 }}>
              Your SMS credits
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.75rem', sm: '2rem' }, lineHeight: 1.2 }}>
              {formatCredits(wallet?.credits)}{' '}
              <Typography component="span" sx={{ fontSize: '1rem', fontWeight: 500, opacity: 0.8 }}>
                credits
              </Typography>
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <PageBlockTitle gutterBottom>Buy SMS credits</PageBlockTitle>
      <Typography sx={{ fontSize: '0.875rem', color: acme.colors.mutedForegroundSolid, mb: 2 }}>
        Credits are used to send SMS campaigns. The number of credits per message depends on the
        destination country (see the Pricing page). Bigger packs include bonus credits. Credits are
        non-refundable.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        {packages.map((pkg) => (
          <Card
            key={pkg.id}
            sx={{
              ...acmePlanCardSx(false, false),
              width: 'auto',
              position: 'relative',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: acme.shadow.lg },
            }}
          >
            {pkg.bonusPercent > 0 && (
              <Chip
                label={`+${pkg.bonusPercent}% bonus`}
                size="small"
                sx={{ ...acmePlanBadgeSx, position: 'absolute', top: 8, right: 8, mb: 0 }}
              />
            )}
            <CardContent
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                p: 2,
                '&:last-child': { pb: 2 },
              }}
            >
              <MessageSquare size={28} strokeWidth={1.5} color={acmeIconTones.default} />
              <Typography sx={{ fontWeight: 700, fontSize: '1.5rem', mt: 1, color: acme.colors.foreground }}>
                {formatCredits(pkg.credits)}
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', color: acme.colors.mutedForegroundSolid }}>
                credits
              </Typography>
              {pkg.bonusCredits > 0 && (
                <Typography sx={{ fontSize: '0.75rem', color: acme.colors.success, fontWeight: 600 }}>
                  incl. {formatCredits(pkg.bonusCredits)} bonus
                </Typography>
              )}
              <Divider sx={{ width: '100%', my: 1.5, borderColor: acme.colors.border }} />
              <Typography sx={{ fontWeight: 700, fontSize: '1.125rem', mb: 2, color: acme.colors.foreground }}>
                {formatMoney(pkg.amount, currency)}
              </Typography>
              <Button
                fullWidth
                sx={{ mt: 'auto' }}
                disabled={busyId !== null}
                onClick={() => startCheckout(pkg.amount, pkg.id)}
              >
                {busyId === pkg.id ? <CircularProgress size={22} color="inherit" /> : 'Buy'}
              </Button>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Card sx={{ boxShadow: 'none' }}>
        <CardContent sx={acmeBillingSectionContentSx}>
          <PageBlockTitle gutterBottom>Or choose your own amount</PageBlockTitle>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <Input
              type="number"
              label="Amount to pay"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              inputProps={{ min: wallet?.minTopup ?? 5, step: '1' }}
              InputProps={{
                startAdornment: <InputAdornment position="start">€</InputAdornment>,
              }}
              sx={{ ...acmeTextFieldSx, width: 220 }}
              helperText={
                estimatedCustomCredits != null
                  ? `≈ ${formatCredits(estimatedCustomCredits)} credits`
                  : `Min ${formatMoney(wallet?.minTopup ?? 5, currency)} · bonus from ${formatMoney(50, currency)}`
              }
            />
            <Button disabled={busyId !== null} onClick={handleCustomTopup} sx={{ mt: 0.5 }}>
              {busyId === 'custom' ? <CircularProgress size={22} color="inherit" /> : 'Buy credits'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Alert severity="info" sx={{ mt: 3, borderRadius: acme.radius.md }}>
        Credits are added to your account immediately after payment. Campaigns are charged per
        successful message based on the destination country; failed messages are not charged.
        Credits are non-refundable. See per-country credit costs on the Pricing page.
      </Alert>
    </Box>
  );
}
